import React, { useState, useMemo } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    Legend
} from 'recharts';
import { useThemeColors } from '../../hooks/useThemeColors';
import './RiskScoreChart.css';

/**
 * RiskScoreChart - Line chart showing risk score over time
 * Supports overall (averaged) view and per-asset multi-line view.
 *
 * @param {Array}  data - Overall averaged data: [{ date, score, total?, critical? }]
 * @param {object} perAssetHistory - Per-asset data: { assetId: { label, assetType, history: [{ date, score }] } }
 * @param {boolean} showVulnCount
 * @param {string} title
 */
function RiskScoreChart({ data = [], perAssetHistory = null, showVulnCount = false, title = 'Risk Score Over Time' }) {
    const themeColors = useThemeColors();
    const [viewMode, setViewMode] = useState('overall');
    const [selectedAssets, setSelectedAssets] = useState(new Set());

    const hasPerAsset = perAssetHistory && Object.keys(perAssetHistory).length > 0;
    const assetIds = useMemo(() => hasPerAsset ? Object.keys(perAssetHistory) : [], [hasPerAsset, perAssetHistory]);
    const assetCount = assetIds.length;

    // Format date for display
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Determine which assets to show in per-asset mode
    const visibleAssetIds = useMemo(() => {
        if (!hasPerAsset) return [];
        if (selectedAssets.size > 0) {
            return assetIds.filter(id => selectedAssets.has(id));
        }
        // Default: show up to 5
        return assetIds.slice(0, 5);
    }, [hasPerAsset, assetIds, selectedAssets]);

    // Build unified per-asset chart data
    const perAssetChartData = useMemo(() => {
        if (!hasPerAsset || viewMode !== 'per-asset') return [];

        const allDates = new Set();
        for (const assetId of visibleAssetIds) {
            const assetData = perAssetHistory[assetId];
            if (assetData) {
                assetData.history.forEach(h => allDates.add(h.date));
            }
        }

        const sortedDates = Array.from(allDates).sort();
        return sortedDates.map(date => {
            const point = { date };
            for (const assetId of visibleAssetIds) {
                const assetData = perAssetHistory[assetId];
                if (assetData) {
                    const entry = assetData.history.find(h => h.date === date);
                    point[assetId] = entry ? entry.score : null;
                }
            }
            return point;
        });
    }, [hasPerAsset, viewMode, visibleAssetIds, perAssetHistory]);

    // Toggle asset filter pill
    const toggleAssetFilter = (assetId) => {
        setSelectedAssets(prev => {
            const next = new Set(prev);
            if (next.has(assetId)) {
                next.delete(assetId);
            } else {
                next.add(assetId);
            }
            // If all are deselected, clear (show default)
            if (next.size === 0) return new Set();
            return next;
        });
    };

    // PRD-51: Dynamic Y-axis — default 0-100, auto-expand if scores exceed 100
    const yAxisMax = useMemo(() => {
        let maxScore = 0;
        if (data && data.length > 0) {
            maxScore = Math.max(maxScore, ...data.map(d => d.score || 0));
        }
        if (hasPerAsset && perAssetHistory) {
            for (const assetId of Object.keys(perAssetHistory)) {
                const history = perAssetHistory[assetId]?.history || [];
                maxScore = Math.max(maxScore, ...history.map(h => h.score || 0));
            }
        }
        return maxScore > 100 ? Math.ceil(maxScore * 1.1 / 50) * 50 : 100;
    }, [data, hasPerAsset, perAssetHistory]);

    const yTicks = useMemo(() => {
        if (yAxisMax <= 100) return [0, 20, 40, 60, 80, 100];
        const step = Math.ceil(yAxisMax / 5 / 50) * 50;
        const ticks = [];
        for (let i = 0; i <= yAxisMax; i += step) ticks.push(i);
        return ticks;
    }, [yAxisMax]);

    // PRD-51: Risk color mapping (higher = riskier)
    const getRiskColor = (score) => {
        if (score >= 80) return themeColors.severity.critical;
        if (score >= 60) return themeColors.severity.high;
        if (score >= 40) return themeColors.severity.medium;
        if (score >= 20) return themeColors.risk.healthy;
        return themeColors.accent.primary;
    };

    // Custom tooltip for overall mode
    const OverallTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-date">{formatDate(label)}</p>
                    <p className="tooltip-score">
                        <span className="tooltip-label">Risk Score:</span>
                        <span className="tooltip-value" style={{
                            color: getRiskColor(d.score)
                        }}>{d.score}</span>
                    </p>
                    {showVulnCount && d.total !== undefined && (
                        <>
                            <p className="tooltip-item">
                                <span className="tooltip-label">Total Vulns:</span>
                                <span className="tooltip-value">{d.total}</span>
                            </p>
                            <p className="tooltip-item">
                                <span className="tooltip-label">Critical:</span>
                                <span className="tooltip-value" style={{ color: themeColors.severity.critical }}>
                                    {d.critical}
                                </span>
                            </p>
                        </>
                    )}
                </div>
            );
        }
        return null;
    };

    // Custom tooltip for per-asset mode
    const PerAssetTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-date">{formatDate(label)}</p>
                    {payload.map((entry, i) => (
                        entry.value != null && (
                            <p key={i} className="tooltip-item">
                                <span className="tooltip-dot" style={{ background: entry.color }}></span>
                                <span className="tooltip-label">{entry.name}:</span>
                                <span className="tooltip-value" style={{ color: entry.color }}>
                                    {entry.value}
                                </span>
                            </p>
                        )
                    ))}
                </div>
            );
        }
        return null;
    };

    // Handle empty data
    const isEmpty = viewMode === 'overall'
        ? (!data || data.length === 0)
        : perAssetChartData.length === 0;

    if (isEmpty && (!hasPerAsset || viewMode === 'overall')) {
        return (
            <div className="risk-score-chart">
                <h3 className="chart-title">{title}</h3>
                {hasPerAsset && (
                    <div className="chart-view-toggle">
                        <button className="view-toggle-btn active" onClick={() => setViewMode('overall')}>Overall</button>
                        <button className="view-toggle-btn" onClick={() => setViewMode('per-asset')}>Per-Asset</button>
                    </div>
                )}
                <div className="chart-empty">
                    <p>No trend data available yet</p>
                    <p className="empty-hint">Run scans on your assets to see risk trends</p>
                </div>
            </div>
        );
    }

    const isPerAsset = viewMode === 'per-asset' && hasPerAsset;
    const chartData = isPerAsset ? perAssetChartData : data;

    return (
        <div className="risk-score-chart">
            <div className="chart-title-row">
                <h3 className="chart-title">{title}</h3>
                {hasPerAsset && (
                    <div className="chart-view-toggle">
                        <button
                            className={`view-toggle-btn ${viewMode === 'overall' ? 'active' : ''}`}
                            onClick={() => setViewMode('overall')}
                        >
                            Overall
                        </button>
                        <button
                            className={`view-toggle-btn ${viewMode === 'per-asset' ? 'active' : ''}`}
                            onClick={() => setViewMode('per-asset')}
                        >
                            Per-Asset
                        </button>
                    </div>
                )}
            </div>

            {/* Asset filter pills for per-asset mode with >5 assets */}
            {isPerAsset && assetCount > 5 && (
                <div className="chart-asset-filter">
                    {assetIds.map((assetId) => {
                        const isActive = selectedAssets.size === 0
                            ? assetIds.indexOf(assetId) < 5
                            : selectedAssets.has(assetId);
                        return (
                            <button
                                key={assetId}
                                className={`asset-filter-pill ${isActive ? 'active' : ''}`}
                                onClick={() => toggleAssetFilter(assetId)}
                            >
                                {perAssetHistory[assetId]?.label || assetId}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke={themeColors.border.dark}
                            vertical={false}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            stroke={themeColors.text.secondary}
                            tick={{ fill: themeColors.text.secondary, fontSize: 12 }}
                            axisLine={{ stroke: themeColors.border.dark }}
                        />
                        <YAxis
                            domain={[0, yAxisMax]}
                            stroke={themeColors.text.secondary}
                            tick={{ fill: themeColors.text.secondary, fontSize: 12 }}
                            axisLine={{ stroke: themeColors.border.dark }}
                            ticks={yTicks}
                        />
                        <Tooltip content={isPerAsset ? <PerAssetTooltip /> : <OverallTooltip />} />

                        {/* PRD-51: Risk level reference lines (higher = riskier) */}
                        <ReferenceLine y={80} stroke={themeColors.severity.critical} strokeDasharray="5 5" strokeOpacity={0.4} />
                        <ReferenceLine y={60} stroke={themeColors.severity.high} strokeDasharray="5 5" strokeOpacity={0.4} />
                        <ReferenceLine y={40} stroke={themeColors.severity.medium} strokeDasharray="5 5" strokeOpacity={0.4} />
                        <ReferenceLine y={20} stroke={themeColors.risk.healthy} strokeDasharray="5 5" strokeOpacity={0.4} />

                        {isPerAsset ? (
                            <>
                                {visibleAssetIds.map((assetId, index) => (
                                    <Line
                                        key={assetId}
                                        type="monotone"
                                        dataKey={assetId}
                                        name={perAssetHistory[assetId]?.label || assetId}
                                        stroke={themeColors.chart[index % themeColors.chart.length]}
                                        strokeWidth={2}
                                        dot={{ fill: themeColors.chart[index % themeColors.chart.length], strokeWidth: 0, r: 3 }}
                                        activeDot={{ r: 5, strokeWidth: 2, stroke: themeColors.bg.primary }}
                                        connectNulls={true}
                                    />
                                ))}
                                <Legend
                                    wrapperStyle={{ fontSize: '0.75rem', paddingTop: '8px' }}
                                    formatter={(value) => <span style={{ color: themeColors.text.secondary }}>{value}</span>}
                                />
                            </>
                        ) : (
                            <Line
                                type="monotone"
                                dataKey="score"
                                stroke={themeColors.accent.primary}
                                strokeWidth={2}
                                dot={{ fill: themeColors.accent.primary, strokeWidth: 0, r: 4 }}
                                activeDot={{ r: 6, fill: themeColors.accent.primary, stroke: themeColors.bg.primary, strokeWidth: 2 }}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* PRD-51: Legend for overall mode (higher = riskier) */}
            {!isPerAsset && (
                <div className="chart-legend">
                    <div className="legend-item">
                        <span className="legend-line" style={{ background: themeColors.severity.critical }}></span>
                        <span>Critical (80+)</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-line" style={{ background: themeColors.severity.high }}></span>
                        <span>High (60-79)</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-line" style={{ background: themeColors.severity.medium }}></span>
                        <span>Medium (40-59)</span>
                    </div>
                    <div className="legend-item">
                        <span className="legend-line" style={{ background: themeColors.risk.healthy }}></span>
                        <span>Healthy (&lt;20)</span>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RiskScoreChart;
